import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { CheckboxMui } from "@ryde/controls";
import {
  NO_VALUE_DISPLAY,
  resolveCourseScheduleExistingRideStatusText,
  RideStatusEnum,
} from "@ryde/shared";

import { CoursesCalendarRideProduceCourseLink } from "./courses-calendar-ride-produce-course-link";
import { CoursesCalendarRideProduceRideLink } from "./courses-calendar-ride-produce-ride-link";
import { ICoursesCalendarRideProduceTableRowProps } from "./courses-calendar-ride-produce.models";
import {
  formatCoursesCalendarProduceDirectionLabel,
  getCoursesCalendarProduceAlreadyCreatedLabelKey,
  getCoursesCalendarProduceDirectionClassName,
  isCoursesCalendarProduceSlotSkipped,
} from "./courses-calendar-ride-produce.utils";

export const CoursesCalendarRideProduceTableRow: FC<
  ICoursesCalendarRideProduceTableRowProps
> = ({ slot, isSelected, onToggleSelected }) => {
  const { t } = useTranslation();
  const skipProduce = isCoursesCalendarProduceSlotSkipped(slot);
  const alreadyCreatedLabelKey =
    getCoursesCalendarProduceAlreadyCreatedLabelKey(slot);
  const rideStatusText = resolveCourseScheduleExistingRideStatusText({
    t,
    existingRideStatus: slot.rideStatus,
  });

  const handleToggle = useEvent((): void => {
    if (skipProduce) {
      return;
    }
    onToggleSelected(slot.id);
  });

  return (
    <tr
      className={clsx({
        "cc-ride-produce-row--skipped": skipProduce,
        "cc-ride-produce-row--canceled":
          slot.rideStatus === RideStatusEnum.Canceled,
      })}
    >
      <td className="cc-ride-produce-col--check">
        <span
          className={clsx("cc-ride-produce-check", {
            "cc-ride-produce-check--disabled": skipProduce,
          })}
        >
          <CheckboxMui
            width={15}
            height={15}
            checked={!skipProduce && isSelected}
            onCheckboxToggle={skipProduce ? undefined : handleToggle}
          />
        </span>
      </td>
      <td>
        <CoursesCalendarRideProduceCourseLink slot={slot} />
      </td>
      <td>{slot.dateKey}</td>
      <td>{slot.timeLabel}</td>
      <td>
        <span
          className={getCoursesCalendarProduceDirectionClassName(
            slot.direction,
          )}
        >
          {formatCoursesCalendarProduceDirectionLabel(slot.direction)}
        </span>
      </td>
      <td className="cc-ride-produce-col--center">{slot.seatsCount ?? 0}</td>
      <td>
        {slot.hasRide && typeof slot.rideId === "number" ? (
          <div className="cc-ride-produce-ride-cell">
            <span className="cc-ride-produce-ride-id-row">
              <CoursesCalendarRideProduceRideLink rideId={slot.rideId} />
              {rideStatusText ? (
                <span className="cc-ride-produce-ride-status">
                  {`(${rideStatusText})`}
                </span>
              ) : null}
            </span>
            {alreadyCreatedLabelKey ? (
              <span className="cc-ride-produce-skip-reason">
                {t(alreadyCreatedLabelKey)}
              </span>
            ) : null}
          </div>
        ) : (
          NO_VALUE_DISPLAY
        )}
      </td>
    </tr>
  );
};
